import { useEffect, useRef, useState } from "react";

interface AwakeningOverlayProps {
  onComplete: () => void;
}

type AwakePhase =
  | "white"      // 真っ白な状態（トンネルの続き）
  | "receding"   // 光が引いていく
  | "whisper"    // テキストが浮かぶ
  | "dissolve"   // 全体が消えていく
  | "done";      // 完了

export default function AwakeningOverlay({ onComplete }: AwakeningOverlayProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animRef = useRef<number>(0);
  const [phase, setPhase] = useState<AwakePhase>("white");
  const progressRef = useRef(1); // 1=白い → 0=透明

  // フェーズタイムライン
  useEffect(() => {
    const t1 = setTimeout(() => setPhase("receding"), 200);
    const t2 = setTimeout(() => setPhase("whisper"),  1800);
    const t3 = setTimeout(() => setPhase("dissolve"), 4200);
    const t4 = setTimeout(() => {
      setPhase("done");
      onComplete();
    }, 6000);
    return () => { clearTimeout(t1); clearTimeout(t2); clearTimeout(t3); clearTimeout(t4); };
  }, [onComplete]);

  // Canvas：光が引いていくアニメーション
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    canvas.width = window.innerWidth;
    canvas.height = window.innerHeight;

    const cx = canvas.width / 2;
    const cy = canvas.height / 2;

    // 光の粒子（外側から中心に向かって収束していく）
    interface Mote {
      x: number; y: number;
      vx: number; vy: number;
      opacity: number;
      size: number;
      life: number;
      maxLife: number;
    }
    const motes: Mote[] = Array.from({ length: 80 }, () => {
      const angle = Math.random() * Math.PI * 2;
      const dist = 100 + Math.random() * Math.max(canvas.width, canvas.height) * 0.5;
      return {
        x: cx + Math.cos(angle) * dist,
        y: cy + Math.sin(angle) * dist,
        vx: (cx - (cx + Math.cos(angle) * dist)) * 0.003,
        vy: (cy - (cy + Math.sin(angle) * dist)) * 0.003,
        opacity: 0.3 + Math.random() * 0.5,
        size: 1 + Math.random() * 3,
        life: Math.random() * 60,
        maxLife: 120 + Math.random() * 180,
      };
    });

    let t = 0;

    function draw() {
      t++;
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      // progressRef を phase に合わせて更新
      if (phase === "receding" || phase === "whisper") {
        progressRef.current = Math.max(progressRef.current - 0.006, 0);
      } else if (phase === "dissolve") {
        progressRef.current = Math.max(progressRef.current - 0.012, 0);
      }

      const p = progressRef.current;

      // ── 白いベース（引いていく） ──
      ctx.fillStyle = `rgba(255, 248, 220, ${p * 0.92})`;
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      // ── 中央から広がる金の光輪（p が下がるにつれ消える） ──
      if (p > 0.05) {
        const ringR = (1 - p) * Math.max(canvas.width, canvas.height) * 0.6;
        if (ringR > 10) {
          const ringGrd = ctx.createRadialGradient(cx, cy, ringR * 0.7, cx, cy, ringR);
          ringGrd.addColorStop(0, `rgba(255, 230, 140, 0)`);
          ringGrd.addColorStop(0.5, `rgba(255, 210, 80, ${p * 0.15})`);
          ringGrd.addColorStop(1, `rgba(255, 200, 60, 0)`);
          ctx.beginPath();
          ctx.arc(cx, cy, ringR, 0, Math.PI * 2);
          ctx.fillStyle = ringGrd;
          ctx.fill();
        }
      }

      // ── 光の粒子（中心に向かって収束） ──
      motes.forEach((m) => {
        m.life++;
        m.x += m.vx * (1 + (1 - p) * 2);
        m.y += m.vy * (1 + (1 - p) * 2);
        const progress = m.life / m.maxLife;
        const alpha = progress < 0.3
          ? (progress / 0.3) * m.opacity * p
          : (1 - (progress - 0.3) / 0.7) * m.opacity * p;
        if (m.life >= m.maxLife) {
          const angle = Math.random() * Math.PI * 2;
          const dist = 80 + Math.random() * Math.max(canvas.width, canvas.height) * 0.45;
          m.x = cx + Math.cos(angle) * dist;
          m.y = cy + Math.sin(angle) * dist;
          m.vx = (cx - m.x) * 0.003;
          m.vy = (cy - m.y) * 0.003;
          m.life = 0;
          m.maxLife = 120 + Math.random() * 180;
        }
        if (alpha <= 0) return;
        ctx.beginPath();
        ctx.arc(m.x, m.y, m.size * p, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(255, 220, 120, ${alpha})`;
        ctx.fill();
      });

      // ── 中央の残光（最後まで残る） ──
      const centerGlow = ctx.createRadialGradient(cx, cy, 0, cx, cy, 120 * p);
      centerGlow.addColorStop(0, `rgba(255, 245, 200, ${p * 0.4})`);
      centerGlow.addColorStop(0.5, `rgba(255, 220, 100, ${p * 0.15})`);
      centerGlow.addColorStop(1, "rgba(255, 200, 60, 0)");
      ctx.beginPath();
      ctx.arc(cx, cy, 120 * p, 0, Math.PI * 2);
      ctx.fillStyle = centerGlow;
      ctx.fill();

      animRef.current = requestAnimationFrame(draw);
    }

    animRef.current = requestAnimationFrame(draw);
    return () => cancelAnimationFrame(animRef.current);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase]);

  if (phase === "done") return null;

  return (
    <div
      className="fixed inset-0 z-40 flex items-center justify-center"
      style={{ pointerEvents: phase === "white" || phase === "receding" ? "all" : "none" }}
    >
      {/* 光のキャンバス */}
      <canvas
        ref={canvasRef}
        className="absolute inset-0"
        style={{ width: "100vw", height: "100vh" }}
      />

      {/* テキスト：光が引いてきたら浮かぶ */}
      <div
        className="relative z-10 text-center px-8"
        style={{ maxWidth: "480px" }}
      >
        {/* 上の線 */}
        <div
          className="mx-auto mb-8"
          style={{
            width: phase === "whisper" ? "60px" : "0px",
            height: "1px",
            background: "rgba(100, 60, 10, 0.35)",
            transition: "width 1.5s ease 0.3s",
          }}
        />

        {/* メッセージ */}
        <p
          style={{
            fontFamily: "'Cormorant Garamond', serif",
            color: "rgba(60, 35, 5, 0.65)",
            fontSize: "clamp(0.9rem, 2.5vw, 1.2rem)",
            fontWeight: 300,
            lineHeight: 2.2,
            letterSpacing: "0.14em",
            fontStyle: "italic",
            opacity: phase === "whisper" ? 1 : 0,
            transform: phase === "whisper" ? "translateY(0)" : "translateY(8px)",
            transition: phase === "dissolve"
              ? "opacity 1.5s ease, transform 1.5s ease"
              : "opacity 1.8s ease 0.5s, transform 1.8s ease 0.5s",
          }}
        >
          目が覚める。
          <br />
          <span style={{ color: "rgba(120, 80, 10, 0.7)", fontSize: "0.85em", letterSpacing: "0.2em" }}>
            ここが、その場所だ。
          </span>
        </p>

        {/* 下の線 */}
        <div
          className="mx-auto mt-8"
          style={{
            width: phase === "whisper" ? "30px" : "0px",
            height: "1px",
            background: "rgba(100, 60, 10, 0.25)",
            transition: "width 1.5s ease 0.6s",
          }}
        />
      </div>
    </div>
  );
}
